import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Edit2, Trash2, Search as SearchIcon } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";

export default function ChannelsPage() {
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const CHANNEL_TYPES = getValues("channel_types");
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [form, setForm] = useState({ name: "", type: "", payment_terms: "", payment_type: "", credit_days: 0, bill_to_bill_max_days: 30, block_grace_days: 10, max_discount: 0, status: "active", enforce_carton_multiples: true });

  const [showRemovedCh, setShowRemovedCh] = useState(false);   // PASS 2 (B8)

  const loadChannels = async (removedToo = showRemovedCh) => {
    try { const { data } = await api.get("/channels", { params: { include_removed: removedToo } }); setChannels(data); }
    catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };

  useEffect(() => { loadChannels(); }, []);

  const openCreate = () => {
    setEditingChannel(null);
    setForm({ name: "", type: "", payment_terms: "", payment_type: "", credit_days: 0, bill_to_bill_max_days: 30, block_grace_days: 10, max_discount: 0, status: "active", enforce_carton_multiples: true });
    setShowDialog(true);
  };

  const openEdit = (ch) => {
    setEditingChannel(ch);
    setForm({ name: ch.name, type: ch.type || "", payment_terms: ch.payment_terms || "", payment_type: ch.payment_type || "", credit_days: ch.credit_days || 0, bill_to_bill_max_days: ch.bill_to_bill_max_days || 30, block_grace_days: ch.block_grace_days ?? 10, max_discount: ch.max_discount || 0, status: ch.status || "active", enforce_carton_multiples: ch.enforce_carton_multiples !== false });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      // blank grace days means "fall back to the 10-day default"
      const body = { ...form, block_grace_days: form.block_grace_days === "" ? null : form.block_grace_days };
      if (editingChannel) {
        const { data } = await api.put(`/channels/${editingChannel._id}`, body);
        // NEXT BUILD PASS 1B (item 10d): the open orders are re-dated for the new payment type
        toast.success(data?.orders_redated
          ? `Channel updated — ${data.orders_redated} open order(s) re-dated`
          : "Channel updated");
      } else {
        await api.post("/channels", body);
        toast.success("Channel created");
      }
      setShowDialog(false);
      loadChannels();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // PASS 2 (B8): a channel is never deleted — it is marked Inactive and kept, because orders,
  // customers and the payment history all hang off it.
  const handleDelete = async (id) => {
    if (!window.confirm("Remove this channel? It is marked Inactive and disappears from the pickers; every order and customer keeps its link to it. An Admin can restore it.")) return;
    try { const { data } = await api.delete(`/channels/${id}`); toast.success(data.message || "Channel removed"); loadChannels(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleRestore = async (id, status) => {
    try { const { data } = await api.post(`/channels/${id}/restore`, { status }); toast.success(data.message); loadChannels(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="channels-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Sales Channels</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your distribution channels</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-zinc-600 cursor-pointer" data-testid="channels-show-removed">
            <input type="checkbox" checked={showRemovedCh}
              onChange={e => { setShowRemovedCh(e.target.checked); loadChannels(e.target.checked); }} />
            Show removed
          </label>
          <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-channel-button">
            <Plus size={14} /> Add Channel
          </Button>
        </div>
      </div>

      <div className="border border-zinc-200 overflow-hidden">
        <table className="data-table w-full" data-testid="channels-table">
          <thead>
            <tr><th>Channel Name</th><th>Type</th><th>Payment Terms</th><th className="num">Max Discount %</th><th>Status</th><th className="w-20">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            channels.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-zinc-400 text-xs">No channels yet</td></tr> :
            channels.map(ch => (
              <tr key={ch._id} data-testid={`channel-row-${ch._id}`}>
                <td className="font-medium text-zinc-900">{ch.name}</td>
                <td>{ch.type || '-'}</td>
                <td data-testid={`channel-terms-${ch._id}`}>{ch.payment_type ? <><span className="font-semibold">{ch.payment_type}</span>{ch.payment_type === "Credit" ? <span className="text-zinc-500"> · {ch.credit_days} days</span> : null}{ch.payment_type === "Bill-to-bill" ? <span className="text-zinc-500"> · max {ch.bill_to_bill_max_days || 30} days</span> : null}</> : <span className="text-amber-700 font-semibold">Payment terms missing</span>}{ch.payment_terms ? <span className="block text-[9px] text-zinc-400">was: {ch.payment_terms}</span> : null}</td>
                <td className="num font-mono">{ch.max_discount}%</td>
                <td>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm ${ch.status === 'active' ? 'status-active' : 'status-inactive'}`} data-testid={`channel-status-${ch._id}`}>{ch.status === 'active' ? 'Active' : 'Inactive'}</span>
                  {ch.is_deleted && <span className="block text-[9px] text-red-600">removed</span>}
                </td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(ch)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`channel-edit-${ch._id}`}><Edit2 size={13} className="text-zinc-500" /></button>
                    {ch.is_deleted
                      ? <button onClick={() => handleRestore(ch._id, "active")} className="p-1 hover:bg-green-50 rounded-sm text-[10px] text-green-700" data-testid={`channel-restore-${ch._id}`}>Restore</button>
                      : <button onClick={() => handleDelete(ch._id)} className="p-1 hover:bg-red-50 rounded-sm" data-testid={`channel-remove-${ch._id}`}><Trash2 size={13} className="text-red-500" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="channel-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingChannel ? "Edit Channel" : "Create Channel"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Channel Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="channel-name-input" /></div>
            <div>
              <Label className="text-xs">Trade Format</Label>
              <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="channel-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{CHANNEL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payment Type *</Label>
              <select value={form.payment_type} onChange={(e) => setForm({...form, payment_type: e.target.value, credit_days: e.target.value === "Credit" ? (form.credit_days || 30) : 0})}
                      className="mt-1 w-full h-9 rounded-sm border border-zinc-300 bg-white px-2 text-sm" data-testid="channel-payment-type">
                <option value="">Select payment type…</option>
                <option value="Advance">Advance</option>
                <option value="Bill-to-bill">Bill-to-bill</option>
                <option value="Credit">Credit</option>
                {/* NEXT BUILD PASS 1B (item 10a) */}
                <option value="Sale or return">Sale or return</option>
              </select>
              {form.payment_type === "Sale or return" && (
                <p className="text-[10px] text-zinc-500 mt-1" data-testid="channel-sor-note">
                  Payment comes as the stock sells. No due date, no overdue block — tracked on SOR Accounts.
                </p>
              )}
              {form.payment_type === "Credit" && (
                <div className="mt-2"><Label className="text-xs">Credit Days *</Label>
                  <Input type="number" min="1" value={form.credit_days} onChange={(e) => setForm({...form, credit_days: parseInt(e.target.value || "0", 10)})}
                         className="mt-1 rounded-sm" data-testid="channel-credit-days" /></div>
              )}
              {/* FOLLOW-UP 4 (8a): how long a rep may keep selling after the due date */}
              <div className="mt-2">
                <Label className="text-xs">Grace days before hard block</Label>
                <Input type="number" min="0" value={form.block_grace_days}
                       onChange={(e) => setForm({...form, block_grace_days: e.target.value === "" ? "" : parseInt(e.target.value, 10)})}
                       className="mt-1 rounded-sm" data-testid="channel-block-grace-days" />
                <p className="text-[10px] text-zinc-400 mt-0.5">Blank means 10 days. Up to this many days after the due date the rep only sees a warning; after it, new orders stop.</p>
              </div>
              {form.payment_type === "Bill-to-bill" && (
                <div className="mt-2"><Label className="text-xs">Maximum days to the next bill</Label>
                  <Input type="number" min="1" value={form.bill_to_bill_max_days} onChange={(e) => setForm({...form, bill_to_bill_max_days: parseInt(e.target.value || "0", 10)})}
                         className="mt-1 rounded-sm" data-testid="channel-btb-max-days" /></div>
              )}
              {form.payment_terms ? <p className="text-[10px] text-zinc-400 mt-1">Old terms text (reference only): {form.payment_terms}</p> : null}
            </div>
            <div><Label className="text-xs">Max Discount %</Label><Input type="number" value={form.max_discount} onChange={(e) => setForm({...form, max_discount: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="channel-discount-input" /></div>
            <div className="flex items-center justify-between"><Label className="text-xs">Enforce carton multiples<br/><span className="text-[9px] text-zinc-400 font-normal">Orders must be in multiples of the SKU&apos;s master carton</span></Label><Switch checked={form.enforce_carton_multiples} onCheckedChange={(v) => setForm({...form, enforce_carton_multiples: v})} data-testid="channel-carton-toggle" /></div>
            {/* PASS 2 (B8): Active / Inactive — an Inactive channel stays on its orders and
                customers but is out of every picker. */}
            <div className="flex items-center justify-between">
              <Label className="text-xs">Status<br/><span className="text-[9px] text-zinc-400 font-normal">Inactive keeps the channel and its history but hides it from new orders and customers</span></Label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="h-8 border border-zinc-300 rounded-sm px-2 text-xs" data-testid="channel-status-select">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm text-xs" data-testid="save-channel-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
